import { useState, useRef, useCallback } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import {
  Upload, Camera, FileText, Save, Loader2, CheckCircle2, AlertTriangle,
  X, ChevronDown, ChevronRight, Edit3, Trash2, Eye, ScanLine
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (v) => {
  const num = Number(v);
  if (isNaN(num)) return String(v);
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 }).format(num);
};

const W2_FIELDS = [
  { section: "Employer Info", fields: [
    { key: "employer_name", label: "Employer Name (Box c)", type: "text" },
    { key: "employer_ein", label: "Employer EIN (Box b)", type: "text" },
    { key: "employer_address", label: "Employer Address", type: "text" },
  ]},
  { section: "Income & Withholding", fields: [
    { key: "box_1_wages", label: "Box 1 - Wages", type: "number" },
    { key: "box_2_federal_withheld", label: "Box 2 - Federal Tax Withheld", type: "number" },
    { key: "box_3_ss_wages", label: "Box 3 - SS Wages", type: "number" },
    { key: "box_4_ss_withheld", label: "Box 4 - SS Tax Withheld", type: "number" },
    { key: "box_5_medicare_wages", label: "Box 5 - Medicare Wages", type: "number" },
    { key: "box_6_medicare_withheld", label: "Box 6 - Medicare Withheld", type: "number" },
  ]},
  { section: "Additional Info", fields: [
    { key: "box_12a_code", label: "Box 12a Code", type: "text" },
    { key: "box_12a_amount", label: "Box 12a Amount", type: "number" },
    { key: "box_12b_code", label: "Box 12b Code", type: "text" },
    { key: "box_12b_amount", label: "Box 12b Amount", type: "number" },
    { key: "box_15_state", label: "Box 15 - State", type: "text" },
    { key: "box_16_state_wages", label: "Box 16 - State Wages", type: "number" },
    { key: "box_17_state_tax", label: "Box 17 - State Tax", type: "number" },
  ]},
];

const FORM_1099_FIELDS = [
  { section: "Payer Info", fields: [
    { key: "form_type", label: "Form Type", type: "select", options: ["1099-DIV", "1099-INT", "1099-B", "1099-NEC", "1099-MISC", "1099-R"] },
    { key: "payer_name", label: "Payer Name", type: "text" },
    { key: "payer_tin", label: "Payer TIN", type: "text" },
  ]},
  { section: "1099-DIV Fields", fields: [
    { key: "div_ordinary", label: "Ordinary Dividends", type: "number" },
    { key: "div_qualified", label: "Qualified Dividends", type: "number" },
    { key: "div_capital_gain", label: "Capital Gain Distributions", type: "number" },
  ]},
  { section: "1099-INT Fields", fields: [
    { key: "int_income", label: "Interest Income", type: "number" },
    { key: "int_federal_withheld", label: "Federal Withheld", type: "number" },
  ]},
  { section: "1099-B Fields", fields: [
    { key: "proceeds", label: "Proceeds", type: "number" },
    { key: "cost_basis", label: "Cost Basis", type: "number" },
    { key: "description", label: "Description", type: "text" },
  ]},
  { section: "1099-NEC/MISC", fields: [
    { key: "nec_compensation", label: "NEC Compensation", type: "number" },
    { key: "misc_rents", label: "Rents", type: "number" },
    { key: "misc_other_income", label: "Other Income", type: "number" },
  ]},
  { section: "1099-R Fields", fields: [
    { key: "retirement_gross", label: "Gross Distribution", type: "number" },
    { key: "retirement_taxable", label: "Taxable Amount", type: "number" },
    { key: "retirement_federal_withheld", label: "Federal Withheld", type: "number" },
  ]},
];

function FieldSection({ title, fields, data, onEdit }) {
  const [open, setOpen] = useState(true);
  const hasData = fields.some(f => {
    const v = data[f.key];
    return v !== undefined && v !== null && v !== 0 && v !== "" && v !== false;
  });

  return (
    <div className={`border rounded-sm overflow-hidden ${hasData ? "border-white/10" : "border-white/5 opacity-60"}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-2.5 bg-white/[0.02] hover:bg-white/[0.03] transition-colors">
        <span className="text-xs font-semibold text-white">{title}</span>
        <div className="flex items-center gap-2">
          {hasData && <span className="w-2 h-2 bg-emerald-500 rounded-full" />}
          {open ? <ChevronDown className="w-3 h-3 text-zinc-500" /> : <ChevronRight className="w-3 h-3 text-zinc-500" />}
        </div>
      </button>
      {open && (
        <div className="divide-y divide-white/5">
          {fields.map(f => (
            <div key={f.key} className="flex items-center gap-3 px-4 py-2">
              <label className="text-xs text-zinc-400 flex-1 min-w-0">{f.label}</label>
              {f.type === "select" ? (
                <Select value={data[f.key] || ""} onValueChange={(v) => onEdit(f.key, v)}>
                  <SelectTrigger className="w-40 bg-black/30 border-white/10 rounded-sm text-xs">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {f.options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  data-testid={`ocr-field-${f.key}`}
                  type={f.type}
                  step={f.type === "number" ? "0.01" : undefined}
                  value={data[f.key] !== undefined ? data[f.key] : ""}
                  onChange={(e) => onEdit(f.key, f.type === "number" ? parseFloat(e.target.value) || 0 : e.target.value)}
                  className="w-40 text-right bg-black/30 border-white/10 rounded-sm text-xs font-mono"
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function TaxOCR() {
  const { api } = useAuth();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [docType, setDocType] = useState("auto");
  const [dragActive, setDragActive] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/webp", "application/pdf"];
    if (!allowed.includes(file.type)) {
      return toast.error("Use JPEG, PNG, WebP, or PDF");
    }
    if (file.size > 15 * 1024 * 1024) {
      return toast.error("File too large (max 15MB)");
    }

    // Preview
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    } else {
      setPreview(null);
    }

    setUploading(true);
    setUploadResult(null);
    setEditedData({});

    const formData = new FormData();
    formData.append("file", file);
    formData.append("doc_type", docType);
    formData.append("tax_year", "2025");

    try {
      const res = await api.post("/tax-ocr/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadResult(res.data);
      if (res.data.extracted_data) {
        setEditedData({ ...res.data.extracted_data });
        toast.success(`${res.data.doc_type} detected — fields extracted!`);
      } else {
        toast.warning("Upload complete but extraction failed. Try with a clearer image.");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [docType]);

  const saveToTaxDocs = async () => {
    if (!uploadResult) return;
    setSaving(true);
    try {
      const res = await api.post(`/tax-ocr/${uploadResult.id}/save`, { edited_data: editedData });
      toast.success(`Saved as ${res.data.doc_type} — now available in Tax Center`);
      setUploadResult(prev => ({ ...prev, status: "saved", saved_to: res.data }));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (key, value) => {
    setEditedData(prev => ({ ...prev, [key]: value }));
  };

  const resetUpload = () => {
    setUploadResult(null);
    setEditedData({});
    setPreview(null);
  };

  const detectedType = editedData.document_type || uploadResult?.doc_type || "W-2";
  const fieldSections = detectedType === "W-2" ? W2_FIELDS : FORM_1099_FIELDS;
  const confidence = editedData.confidence || uploadResult?.extracted_data?.confidence;
  const isSaved = uploadResult?.status === "saved";

  return (
    <Layout title="Tax Document OCR">
      <div data-testid="tax-ocr-page" className="space-y-6">
        {/* Upload Area */}
        {!uploadResult && (
          <>
            <div className="flex items-center gap-3">
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger data-testid="doc-type-select" className="w-48 bg-black/50 border-white/10 rounded-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  <SelectItem value="auto">Auto-Detect Type</SelectItem>
                  <SelectItem value="W-2">W-2 Form</SelectItem>
                  <SelectItem value="1099">1099 Form</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-zinc-500">Upload a photo or scan of your W-2 or 1099</p>
            </div>

            <div
              data-testid="upload-dropzone"
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => fileRef.current?.click()}
              className={`border-2 border-dashed rounded-sm p-12 text-center cursor-pointer transition-all ${
                dragActive ? "border-primary bg-primary/5" : "border-white/10 hover:border-white/20 bg-[#0A0A0A]"
              }`}
            >
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => handleFile(e.target.files?.[0])}
                className="hidden"
              />
              {uploading ? (
                <div className="flex flex-col items-center gap-3">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                  <p className="text-sm text-zinc-400">Uploading and extracting fields...</p>
                  <p className="text-xs text-zinc-600">AI is analyzing your document</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-3">
                  <div className="w-16 h-16 rounded-full bg-white/[0.02] border border-white/5 flex items-center justify-center">
                    <ScanLine className="w-8 h-8 text-zinc-500" />
                  </div>
                  <div>
                    <p className="text-sm text-white font-medium">Drop your W-2 or 1099 here</p>
                    <p className="text-xs text-zinc-500 mt-1">or click to browse — JPEG, PNG, WebP, PDF (max 15MB)</p>
                  </div>
                  <div className="flex gap-4 mt-2">
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                      <Camera className="w-3 h-3" /> Phone photo
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                      <FileText className="w-3 h-3" /> Scanned PDF
                    </div>
                    <div className="flex items-center gap-1.5 text-[10px] text-zinc-500">
                      <Upload className="w-3 h-3" /> Screenshot
                    </div>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Results */}
        {uploadResult && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left: Image Preview */}
            <div className="space-y-3">
              {preview && (
                <Card className="bg-[#0A0A0A] border-white/10 rounded-none overflow-hidden">
                  <img src={preview} alt="Document" className="w-full max-h-[500px] object-contain" />
                </Card>
              )}

              {/* Status Card */}
              <Card className={`rounded-none ${
                isSaved ? "bg-[#0A0A0A] border-emerald-500/20" :
                uploadResult.status === "extracted" ? "bg-[#0A0A0A] border-primary/20" :
                "bg-[#0A0A0A] border-amber-500/20"
              }`}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    {isSaved ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                    ) : uploadResult.status === "extracted" ? (
                      <Eye className="w-5 h-5 text-primary" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-amber-400" />
                    )}
                    <div>
                      <p className="text-sm font-medium text-white">
                        {isSaved ? "Saved to Tax Documents" :
                         uploadResult.status === "extracted" ? "Ready to Review" : "Extraction Failed"}
                      </p>
                      <p className="text-xs text-zinc-500">
                        {detectedType} | {uploadResult.original_filename}
                        {confidence && ` | ${(confidence * 100).toFixed(0)}% confidence`}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Actions */}
              <div className="flex gap-2">
                <Button
                  data-testid="save-tax-doc-btn"
                  onClick={saveToTaxDocs}
                  disabled={saving || isSaved || !uploadResult.extracted_data}
                  className="flex-1 bg-primary text-black font-bold rounded-sm hover:bg-primary/90"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  {isSaved ? "Saved" : "Save to Tax Docs"}
                </Button>
                <Button
                  data-testid="new-upload-btn"
                  onClick={resetUpload}
                  variant="outline"
                  className="border-white/10 hover:bg-white/5 rounded-sm"
                >
                  <Upload className="w-4 h-4 mr-2" /> New Upload
                </Button>
              </div>
            </div>

            {/* Right: Extracted Fields */}
            <div className="lg:col-span-2 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                  <Edit3 className="w-5 h-5 text-primary" />
                  Extracted Fields
                </h2>
                <span className="text-xs text-zinc-500">
                  Review and edit before saving
                </span>
              </div>

              {/* Tax Year & Doc Type */}
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 mb-1 block">Tax Year</label>
                  <Input
                    data-testid="ocr-tax-year"
                    type="number"
                    value={editedData.tax_year || 2025}
                    onChange={(e) => handleEdit("tax_year", parseInt(e.target.value) || 2025)}
                    className="bg-black/30 border-white/10 rounded-sm text-sm font-mono"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-[10px] text-zinc-500 mb-1 block">Document Type</label>
                  <Select value={detectedType} onValueChange={(v) => handleEdit("document_type", v)}>
                    <SelectTrigger className="bg-black/30 border-white/10 rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="W-2">W-2</SelectItem>
                      <SelectItem value="1099">1099</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Field Sections */}
              {fieldSections.map((section, i) => (
                <FieldSection
                  key={i}
                  title={section.section}
                  fields={section.fields}
                  data={editedData}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
